import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import axios from "axios";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

const API_BASE = process.env.REACT_APP_API || "";
const ENDPOINT_ALL_TIME_LOGIN = `${API_BASE}/getAllTimeLogin`;

function formatHourLabel(h) {
  const hh = String(h).padStart(2, "0");
  return `${hh}:00`;
}

function formatHourRange(h) {
  const start = formatHourLabel(h);
  const end = formatHourLabel((h + 1) % 24);
  return `${start}–${end}`;
}

const RoundedBarShape = (props) => {
  const { x, y, width, height, fill } = props;
  const radius = 6;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={Math.max(height, 0)}
        rx={radius}
        ry={radius}
        fill={fill}
      />
    </g>
  );
};

export default function AllTimePeakLoginChart({ year }) {
  const [dataPoints, setDataPoints] = useState([]);
  const [peakInfo, setPeakInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const effectiveYear = year || new Date().getFullYear();

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadData();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveYear]);

  async function loadData() {
    setLoading(true);
    setErr("");

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const res = await axios.get(ENDPOINT_ALL_TIME_LOGIN, {
        headers,
        params: {
          type: "hourly",
          year: effectiveYear,
        },
      });

      const rows = res.data?.data || [];

      const countsMap = new Map();
      rows.forEach((r) => {
        const h = Number(r.hourly);
        if (!Number.isNaN(h)) {
          countsMap.set(h, Number(r.ctr || 0));
        }
      });

      const base = Array.from({ length: 24 }, (_, h) => h);

      const rawData = base.map((h) => ({
        hour: h,
        centerHour: h + 0.5,
        label: formatHourLabel(h),
        value: countsMap.get(h) || 0,
      }));

      const maxVal = rawData.reduce(
        (max, item) => (item.value > max ? item.value : max),
        0
      );

      const finalData = rawData.map((item) => ({
        ...item,
        isPeak: item.value === maxVal && maxVal > 0,
      }));

      setDataPoints(finalData);

      if (maxVal > 0) {
        const firstPeak = finalData.find((d) => d.isPeak);
        if (firstPeak) {
          setPeakInfo({
            hour: firstPeak.hour,  
            count: firstPeak.value,
          });
        } else {
          setPeakInfo(null);
        }
      } else {
        setPeakInfo(null);
      }
    } catch (e) {
      console.error(e);
      setErr("Error loading all-time peak login hour.");
      setDataPoints([]);
      setPeakInfo(null);
    } finally {
      setLoading(false);
    }
  }

  const maxValue = dataPoints.reduce(
    (max, item) => (item.value > max ? item.value : max),
    0
  );

  const barColor = "#3B82F6";
  const peakColor = "#FB923C";

  // ticks at hour boundaries 0..24
  const hourTicks = Array.from({ length: 25 }, (_, i) => i);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" role="status" size="sm" />
      </div>
    );
  }

  if (err) {
    return <div className="text-danger small">{err}</div>;
  }

  return (
    <div style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dataPoints}
          margin={{ top: 10, right: 10, left: 0, bottom: 25 }}
        >
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="centerHour"
            domain={[0, 24]}         
            ticks={hourTicks}       
            tick={{ fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={45}
            tickFormatter={(v) =>
              v === 24 ? "" : formatHourLabel(v) 
            }
          />
          <YAxis
            tick={{ fontSize: 10 }}
            allowDecimals={false}
            label={{
              value: "Login Count",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11 },
            }}
          />
          <Tooltip
            formatter={(value) => [`${value} logins`, "Count"]}
            labelFormatter={(value) => {
              const h = Math.floor(value); 
              return formatHourRange(h);  
            }}
          />
          <Bar dataKey="value" barSize={18} shape={<RoundedBarShape />}>
            {dataPoints.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isPeak ? peakColor : barColor}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div
        className="d-flex justify-content-between align-items-center small"
        style={{ marginTop: "-20px" }}
      >
        {peakInfo && maxValue > 0 && (
          <div>
            <span className="me-2">
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: peakColor,
                  marginRight: 4,
                }}
              ></span>
              Peak hour:
            </span>
            <strong>{formatHourRange(peakInfo.hour)}</strong>{" "}
            <span className="text-muted">({peakInfo.count} logins)</span>
          </div>
        )}
      </div>
    </div>
  );
}
